import { remark } from "remark"
import remarkGfm from "remark-gfm"
import html from "remark-html"
import sanitizeHtml from "sanitize-html"
import type { ImageTransformOptions } from "./imageTransforms"
import { rewriteAssetImageUrlsInHtml } from "./imageTransforms"

export type MarkdownRenderOptions = {
  imageRuntimeOptions?: ImageTransformOptions
  rewriteAssetImages?: boolean
}

const markdownAllowedTags = [
  "a",
  "abbr",
  "blockquote",
  "br",
  "code",
  "del",
  "em",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "i",
  "img",
  "input",
  "li",
  "ol",
  "p",
  "pre",
  "strong",
  "table",
  "tbody",
  "td",
  "th",
  "thead",
  "tr",
  "ul",
]

export async function renderMarkdownHtml(
  markdown: string | null | undefined,
  options: MarkdownRenderOptions = {}
): Promise<string> {
  if (!markdown?.trim()) return ""

  const result = await remark().use(remarkGfm).use(html).process(markdown)
  const sanitized = sanitizeMarkdownHtml(result.toString())

  if (!options.rewriteAssetImages) return sanitized
  return rewriteAssetImageUrlsInHtml(sanitized, "markdown", options.imageRuntimeOptions)
}

export function sanitizeMarkdownHtml(markup = ""): string {
  const normalizedHtml = markup.replace(
    /(\s(?:href|target|rel|title|alt|src|align|class))=`([^`]*)`/g,
    '$1="$2"'
  )
  return sanitizeHtml(normalizedHtml, {
    allowedTags: markdownAllowedTags,
    allowedAttributes: {
      a: ["href", "target", "rel", "title"],
      abbr: ["title"],
      code: ["class"],
      img: ["alt", "decoding", "height", "loading", "src", "title", "width"],
      input: ["checked", "disabled", "type"],
      td: ["align"],
      th: ["align"],
    },
    allowedSchemes: ["http", "https", "mailto"],
    allowedSchemesByTag: {
      img: ["http", "https"],
    },
    exclusiveFilter: (frame) => (frame.tag === "a" && !frame.attribs.href ? "excludeTag" : false),
    transformTags: {
      a: (_tagName, attribs) => ({
        tagName: "a",
        attribs: {
          ...attribs,
          ...(attribs.target === "_blank" ? { rel: "noopener noreferrer" } : {}),
        },
      }),
      img: (_tagName, attribs) => ({
        tagName: "img",
        attribs: {
          ...attribs,
          alt: attribs.alt ?? "",
          decoding: "async",
          loading: "lazy",
        },
      }),
      input: (_tagName, attribs) => ({
        tagName: "input",
        attribs: {
          ...attribs,
          disabled: "disabled",
          type: attribs.type === "checkbox" ? "checkbox" : "",
        },
      }),
    },
  })
}
